import { ReactNode, useContext, useMemo } from 'react';
import { MaskType } from '@givebutter/primitives/input';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { maskDecimal, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import {
    IconDefinition,
    faDollar,
    faHashtag,
    faMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons';
import { NegationOptions } from 'components/filter/NegationOptions';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import {
    DonorsearchValue,
    isDonorSearchByServerKey,
    isDonorSearchFilterValue,
    preprocessTransformUpstream,
    transformUpstream,
} from 'components/filter/filters-next/donorsearch';
import { operatorClassName } from 'components/filter/filters-next/props';
import { transformOperatorToReadable } from 'components/filter/filters-next/readable';
import {
    hasPossibleNegationOperator,
    transformRelationalResourceToReadable,
} from 'components/filter/filters-next/relational';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { transformValueToRequest } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterProps,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
    UpstreamPreprocessTransform,
} from 'components/filter/types';
import { ValidationTextMap } from 'components/filter/validations';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { capitalize, identity, toLower } from 'lodash';
import { twJoin } from 'tailwind-merge';

interface AmountFilterFormValues extends FilterFormValue<string> {}
interface DynamicProps {
    mask?: ReturnType<typeof getMaskByServerKey>;
    maskType?: ReturnType<typeof getMaskTypeByServerKey>;
    label?: ReactNode;
}

export const filterKey: ServerKey[] = [
    'amount',
    'AnnualFundLikelihood',
    'MajorGiftLikelihood',
    'LikelyMatchesTotal',
    'LargestGiftLow',
    'recurring_contributions',
    'total_contributions',
    'total_soft_credits',
    'donors',
    'goal',
    'raised',
    'table_count',
    'total',
    'addressCity',
    'addressState',
    'addressZip',
    'amountFulfilled',
    'remainingAmount',
];

export const Icon = (props: FilterProps & { icon?: IconDefinition }) => {
    const { icon, strategyType } = props;

    if (icon) {
        return icon;
    }

    switch (getMaskTypeByServerKey(strategyType)) {
        case 'decimal':
            return faDollar;
        case 'whole-number':
            return faHashtag;
        default:
            return faMagnifyingGlass;
    }
};

export const Filter: ContentFilterComponent = (props: DynamicProps) => {
    const { label, maskType: maskConsumer } = props;
    const { defaultValue, ...rest } = useFilter<string>();
    const { relationalServerKey, strategyType: strategyTypeRaw } = useContext(FilterContext);
    const strategyType = strategyTypeRaw as ServerKey;
    const relationalResource = relationalServerKey
        ? transformRelationalResourceToReadable(relationalServerKey)
        : '';
    const mask = maskConsumer ?? getMaskTypeByServerKey(strategyType);
    const hasMask = mask !== 'none';
    const defaultNegationOperator = relationalServerKey ? 'has' : undefined;
    const defaultFilterOperator = hasMask ? 'gt' : 'eq';
    const methods = Form.useForm<AmountFilterFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : '',
            filterOperator: defaultValue?.filterOperator
                ? defaultValue.filterOperator
                : defaultFilterOperator,
            negationOperator: defaultValue?.negationOperator
                ? defaultValue.negationOperator
                : defaultNegationOperator,
        },
    });

    const { path, control } = methods;

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container className={'gap-3'}>
                            <div className={'flex flex-col'}>
                                {relationalServerKey &&
                                    hasPossibleNegationOperator(
                                        strategyType,
                                        relationalServerKey,
                                    ) && (
                                        <>
                                            <Form.SingleSelect
                                                control={control}
                                                name="negationOperator"
                                            >
                                                <SingleSelect.Trigger
                                                    aria-label="Negation operator"
                                                    className={operatorClassName}
                                                    size="sm"
                                                />
                                                <SingleSelect.Content>
                                                    <NegationOptions
                                                        resource={relationalResource}
                                                    />
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                            <Form.Label
                                                className={'sr-only'}
                                                name={path('negationOperator')}
                                            >
                                                Negation operator
                                            </Form.Label>
                                        </>
                                    )}
                                <div className={'flex items-center gap-1'}>
                                    <Form.Label name={path('value')}>
                                        {relationalServerKey && <>of&nbsp;</>}
                                        {label ?? (
                                            <span
                                                className={twJoin(
                                                    relationalServerKey && 'lowercase',
                                                )}
                                            >
                                                <Label
                                                    strategyType={strategyType}
                                                    relationalServerKey={relationalServerKey}
                                                />
                                            </span>
                                        )}
                                    </Form.Label>
                                    {hasMask && (
                                        <>
                                            <Form.SingleSelect
                                                control={control}
                                                name="filterOperator"
                                            >
                                                <SingleSelect.Trigger
                                                    aria-label="Negation operator"
                                                    className={operatorClassName}
                                                    size="sm"
                                                />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="gt">
                                                        <SingleSelect.ItemText>
                                                            Greater than
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="lt">
                                                        <SingleSelect.ItemText>
                                                            Less than
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="eq">
                                                        <SingleSelect.ItemText>
                                                            Equal to
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                            <Form.Label
                                                className={'sr-only'}
                                                name={path('filterOperator')}
                                            >
                                                Operator
                                            </Form.Label>
                                        </>
                                    )}
                                </div>
                            </div>
                            <Form.Container>
                                <Form.TextInputRoot
                                    rules={{ required: ValidationTextMap.Required }}
                                    name={'value'}
                                    control={control}
                                    inputRootProps={{ mask }}
                                >
                                    {mask === 'decimal' && <InputCurrencyIcon />}
                                    <Form.TextInputForm autoFocus={true} />
                                </Form.TextInputRoot>
                                <Form.Error name={path('value')} />
                            </Form.Container>
                        </Form.Container>
                        <FilterSubmit />
                    </FilterContainer>
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};

export const Label: LabelFilterComponent = props => {
    const { strategyType, relationalServerKey } = props;
    const { resource } = useContext(FilterContext);

    switch (strategyType) {
        case 'DS_Rating':
            return 'Donorsearch Rating';
        case 'MajorGiftLikelihood':
            return 'Major Gift Likelihood';
        case 'AnnualFundLikelihood':
            return 'Annual Gift Likelihood';
        case 'LikelyMatchesTotal':
            return ' Total Likely External Gift Amount';
        case 'LargestGiftLow':
            return 'Lowest External Gift Amount';
        case 'total_contributions':
            return 'Total contributions';
        case 'total_soft_credits':
            return 'Total soft credits';
        case 'recurring_contributions':
            return 'Recurring contributions';
        case 'raised':
            return 'Amount raised';
        case 'goal':
            return 'Fundraising goal';
        case 'donors':
            return 'Supporter count';
        case 'table_count':
            return `${capitalize(
                relationalServerKey
                    ? transformRelationalResourceToReadable(relationalServerKey)
                    : resource,
            )} count`;
        case 'amount':
            if (resource === 'mailingsTracking') {
                return 'Revenue';
            }
            if (resource === 'pledges') {
                return 'Pledge amount';
            }

            return 'Amount';
        case 'amountFulfilled':
            return 'Amount fulfilled';
        case 'remainingAmount':
            return 'Remaining balance';
        case 'addressCity':
            return 'City';
        case 'addressState':
            return 'State';
        case 'addressZip':
            return 'Zip code';
        case 'hasPrimaryAddress':
            return 'Has primary address';
        default:
            return capitalize(toLower(strategyType));
    }
};

export const Readable: ReadableFilterComponent = (props: DynamicProps) => {
    const { mask: maskConsumer } = props;
    const currentFilter = useCurrentFilter<string>();
    const value = currentFilter?.value ?? '';
    const mask = maskConsumer ?? getMaskByServerKey(currentFilter?.serverKey);
    const currencySymbol = usePossibleCurrencySymbol(!!maskConsumer);

    return (
        <ReadableFilterValue>
            {currentFilter?.filterOperator &&
                transformOperatorToReadable(currentFilter.filterOperator)}{' '}
            {currencySymbol}
            {mask(value)}
        </ReadableFilterValue>
    );
};

const usePossibleCurrencySymbol = (isMaskedOverride?: boolean) => {
    const currentFilter = useCurrentFilter<string>();
    const currencySymbol = useCurrencySymbol();

    if (
        isMaskedOverride ||
        (currentFilter?.serverKey && getMaskTypeByServerKey(currentFilter.serverKey) === 'decimal')
    ) {
        return currencySymbol;
    }

    return '';
};

export const downstream: DownstreamFilterTransform<string> = (value, key) =>
    getMaskByServerKey(key)(value);

export const preprocessUpstream: UpstreamPreprocessTransform<
    string,
    string | DonorsearchValue
> = filter => {
    if (isDonorSearchFilterValue(filter) && isDonorSearchByServerKey(filter.serverKey)) {
        return preprocessTransformUpstream(filter);
    }

    return filter;
};

export const upstream: UpstreamFilterTransform<string | DonorsearchValue> = filter => {
    if (isDonorSearchFilterValue(filter) && isDonorSearchByServerKey(filter.value.key)) {
        const transformed = transformUpstream(filter);

        return {
            ...transformed,
            value: getUnmaskByServerKey(transformed.key)(transformed.value as string),
        };
    }

    return transformValueToRequest(
        filter.value as string,
        'and',
        filter.filterOperator,
        getUnmaskByServerKey(filter.serverKey),
    );
};

const getMaskTypeByServerKey = (key?: ServerKey): MaskType => {
    switch (key) {
        case 'amount':
        case 'amountFulfilled':
        case 'remainingAmount':
        case 'LikelyMatchesTotal':
        case 'LargestGiftLow':
        case 'recurring_contributions':
        case 'total_contributions':
        case 'total_soft_credits':
        case 'raised':
        case 'goal':
        case 'total':
            return 'decimal';
        case 'MajorGiftLikelihood':
        case 'AnnualFundLikelihood':
        case 'table_count':
        case 'donors':
            return 'whole-number';
        default:
            return 'none';
    }
};

const getMaskByServerKey = (key?: ServerKey) => {
    const maskType = getMaskTypeByServerKey(key);

    switch (maskType) {
        case 'whole-number':
            return maskWholeNumber;
        case 'decimal':
            return maskDecimal;
        default:
            return identity;
    }
};

const getUnmaskByServerKey = (key: ServerKey) => {
    const maskType = getMaskTypeByServerKey(key);

    switch (maskType) {
        case 'whole-number':
        case 'decimal':
            return unmaskNumber;
        default:
            return identity;
    }
};
